/**
 * @fileoverview Component for displaying participants in a table format.
 * This component includes a search bar, filters, and pagination.
 */

import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import AiBadge from '../../../assets/ai.svg'
import { States } from '../../../config'
import {
    useGetParticipantsCountForStudyIdQuery,
    useGetParticipantsForStudyIdQuery,
} from '../../../services/participants'
import { formatOutput } from '../../../utils/formatOutput'
import { formatParticipantStatus } from '../../../utils/formatParticipantStatus'
import { Search } from '../../elements/Search'
import { Pagination } from '../../Pagination'
import { Status } from '../../Status'

/**
 * @function formatID
 * @description Formats the participant ID with a prefix and a separator.
 * @param {string} prefix - The prefix to add to the participant ID.
 * @param {string} id - The participant ID.
 * @returns {string} - The formatted participant ID.
 */
function formatID(prefix: string, id: string) {
    return formatOutput(prefix + '–' + id.split('-')[0])
}

/**
 * @function formatParticipant
 * @description Formats the participant details for display.
 * @param {string} participant_id - The participant ID.
 * @param {string} name - The participant name.
 * @param {boolean} recommended - Whether the participant is recommended.
 * @returns {JSX.Element} - A React component that renders the participant details.
 */
function formatParticipant(
    participant_id: string,
    name: string,
    recommended: boolean
): JSX.Element {
    return (
        <div className="flex flex-col gap-y-1">
            <p className="flex text-sm font-medium">
                {formatID('PID', participant_id)}
                {recommended && <AiBadge className="ml-2 w-5 h-5" />}
            </p>
            <p className="text-xs font-regular text-gray-600">{formatOutput(name)}</p>
        </div>
    )
}

/**
 * @function ParticipantTab
 * @description Component for displaying participants in a table format.
 * @returns {JSX.Element} - A React component that renders the participants.
 */
export function ParticipantTab() {
    const navigate = useNavigate()
    const params = useParams()

    const {
        data: participants,
        isLoading,
        refetch: refetchParticipants,
    } = useGetParticipantsForStudyIdQuery(params.studyId)
    const { data: total, refetch: refetchParticipantsCount } =
        useGetParticipantsCountForStudyIdQuery(params.studyId)

    useEffect(() => {
        refetchParticipants()
        refetchParticipantsCount()
    }, [location.search])

    function handleRowClick(participant: Novoic.API.Participant) {
        navigate(`/app/participants/${participant.studies?.[0]?.status}/${participant.id}`)
    }

    const columns = ['Participant', 'Score', 'Stage', 'Site']

    if (isLoading) {
        return null
    }

    return (
        <>
            <div className="flex sticky top-[177px] z-10 gap-x-2 bg-white border-b px-8 py-6">
                <Search placeholder="Search by participant ID or name..." />
                {/* <Filters /> */}
            </div>
            <table className="w-full">
                <thead className="nv-thead top-[266px] sticky z-0">
                    <tr className="h-12">
                        {columns.map((col, index) => (
                            <th
                                key={col}
                                scope="col"
                                className={`${
                                    index === 0 ? 'pl-8 pr-2.5' : 'px-2.5'
                                } text-left text-sm font-medium text-gray-900 whitespace-nowrap`}
                            >
                                <button className="group inline-flex">
                                    {col}
                                    <span className="invisible ml-2 flex-none rounded text-gray-400 group-hover:visible group-focus:visible">
                                        <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                                    </span>
                                </button>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                    {participants.map((participant: Novoic.API.Participant) => (
                        <tr
                            key={participant.id}
                            className="hover:bg-[#f7f9ff] cursor-pointer transition-all"
                            onClick={() => handleRowClick(participant)}
                        >
                            <td
                                className="pl-8 whitespace-nowrap pr-10 py-4 w-[60px]"
                                role="presentation"
                            >
                                {formatParticipant(
                                    participant.studies?.[0]?.external_id || participant.id,
                                    participant.name,
                                    participant.assignment?.recommend
                                )}
                            </td>
                            <td className="px-2.5 whitespace-nowrap pr-10 py-4 w-[60px] text-sm">
                                {formatParticipantStatus(
                                    participant.assignment?.status,
                                    participant.assignment?.score,
                                    participant.assignment?.level,
                                    participant.assignment?.updated_at ||
                                        participant.assignment?.created_at
                                )}
                            </td>
                            <td className="px-2.5 whitespace-nowrap pr-10 py-4 w-[60px] text-sm text-gray-600">
                                <Status
                                    status={participant.studies?.[0]?.status as unknown as States}
                                />
                            </td>
                            <td className="px-2.5 whitespace-nowrap pr-10 py-4 text-sm text-gray-600">
                                {formatOutput(participant.sites?.[0]?.site?.name)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className="flex w-full h-[74px] items-center bg-white border-t border-t-gray-200">
                <Pagination total={total?.count} />
            </div>
        </>
    )
}
